/**
 * @module app.core
 */

import { autoinject, bindable, bindingMode } from 'aurelia-framework';
import { ComponentBase, SortDirection, Sorting, IForm } from 'app/shared/lib/essette-site';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { apiCall } from 'app/shared/lib/api/api';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { MemberService } from 'app/cd/services/member-service';
import { IPopup } from 'app/shared/elements/popup-base';
import { SaveCondition } from 'app/core/components/member/save-condition';
import { validate } from 'app/shared/elements/ess-rule';
import { confirmation } from 'app/shared/elements/ess-confirm';
import { Toaster } from 'app/shared/lib/toaster';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import * as CD from 'app/cd/models/essette-api';

@autoinject
export class memberCodesConditions extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView })
    public memberPlanId: string | null = null;

    @bindable
    protected memberPlanIsActive: boolean = false;

    @bindable
    protected includeInactive: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.toView })
    protected conditions: Array<CD.Condition> = [];

    protected memberConditions: Array<CD.MemberCondition> = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;

    protected validate = validate;

    ////****************** Element References ***************************************////
    protected $saveConditionModal!: IPopup;
    protected $form!: IForm;
    protected $conditionForm!: Element;
    protected $saveCondition!: SaveCondition;
    protected $pager!: EssPagerCustomElement;

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly memberService: MemberService,
        private readonly toaster: Toaster,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        if(!this.memberPlanId) { this.logger.warn('Bindable property [member-plan-id] was not set at binding'); }

        this.reload();
    }

    ////****************** Events ***************************************************////
    protected formSuccess({ detail }: FormSuccessCustomEvent) {
        this.$saveConditionModal.close();

        if(detail.form.reset) detail.form.reset();

        // reload grid and reset page
        this.reload();
    }

    ////****************** Functions ************************************************////
    protected saveCondition(memberCondition: CD.MemberCondition): void {
        this.$saveCondition.memberCondition = memberCondition;
        this.$saveCondition.condition = memberCondition?.condition;
        this.$saveCondition.conditionStart = memberCondition?.dateRange?.start;
        this.$saveCondition.conditionEnd = memberCondition?.dateRange?.end;
        this.$saveConditionModal.open();
    }

    protected saveConditionClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$saveConditionModal.close();
    }

    public async reload(): Promise<void> {
        await this.loadMemberConditionsByPlan();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadMemberConditionsByPlan(): Promise<void> {
        if(!this.memberPlanId) return;

        const options: Partial<IQueryLoadOptions> = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: this.includeInactive
        };

        const includes: string[] = [
            'condition'
        ];

        const fields: string[] = [
            'condition.description',
            'daterange.start',
            'daterange.end'
        ];

        try {
            this.state.beginLoad();

            const response = await this.memberService.loadMemberConditionsByPlan(this.memberPlanId, includes, undefined, options, fields);
            this.memberConditions = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async activateMemberCondition(memberCondition: CD.MemberCondition): Promise<void> {
        if(!memberCondition.id) return;

        // Show confirmation dialog only if deactivating
        if(memberCondition.isActive) {
            const result = await confirmation.showDeactivate('cd:condition.$', 1, false);
            if(!(result.dialogResponse === confirmation.options.OK)) {
                return;
            }
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();
            const response = await this.memberService.activateMemberCondition(memberCondition.id, !memberCondition.isActive);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            this.toaster.standard.deleted('cd:condition.$', false);
            throw error;
        }
        finally {
            this.toaster.standard.activate('cd:condition.$', !memberCondition.isActive, isSuccessful);
            this.state.endSave();
            await this.reload();
        }
    }
}
